/* ==========================================================================
   Metal Museum — custom scripts
   Loaded from snippets/custom-assets.liquid (useCustomScript), rendered at
   layout/theme.liquid:255.
   ========================================================================== */

/* --------------------------------------------------------------------------
   Homepage nav swap: left sidebar over the hero, top bar past it.

   layout/theme.liquid renders both navs on the index template. This toggles
   `nav-topbar` on <body>; assets/custom-styles.css does the rest.

   Why a scroll read rather than an IntersectionObserver: the hero is the FIRST
   section, pinned to the top of the document and viewport-sized, so its bottom edge is
   `heroHeight - scrollY` — a function of scroll position ONLY. Nothing below it
   can move it, which means collapsing the sidebar (and reflowing everything
   underneath) cannot feed back into the trigger. That is what makes this safe.

   Hysteresis is still applied so a scroll resting exactly on the boundary
   cannot flicker: engage at 0, release only once 200px of hero is back in view.
   -------------------------------------------------------------------------- */

(function () {
  'use strict';

  var DESKTOP = '(min-width: 921px)';
  var RELEASE_SLOP = 200; // px of hero back in view before switching back

  var HERO_SEL = 'main .shopify-section:first-child .overlapping-section';

  function findHero() {
    return document.querySelector(HERO_SEL);
  }

  var hero = findHero();

  // Self-gating: only the homepage has an overlapping first section, so every
  // other template exits here and is completely untouched.
  if (!hero) return;

  var body = document.body;
  var active = false;
  var ticking = false;

  // Marks the homepage for the CSS that un-sticks the sidebar. Applied once and
  // never removed, so the sidebar scrolls away with the hero rather than staying
  // pinned over the white section below it.
  body.classList.add('home-nav');

  // sticky_header is "disable" on this section, so nothing writes --header-height.
  // base.css:19182 uses `max-height: calc(90vh - var(--header-height))` for the
  // mega menu; with the variable unset that calc() is invalid and the whole
  // declaration is dropped, letting the mega menu overflow the viewport with no
  // scroll. Publish the real height ourselves.
  function publishHeaderHeight() {
    var bar = document.querySelector('#shopify-section-header .header-section');
    if (!bar) return;
    var h = Math.round(bar.getBoundingClientRect().height);
    if (h > 0) {
      document.documentElement.style.setProperty('--header-height', h + 'px');
    }
  }

  function apply() {
    ticking = false;

    if (!window.matchMedia(DESKTOP).matches) {
      // Below 921px the sidebar and the top header are both display:none and
      // mobile-header owns the nav. Leave no state behind.
      if (active) {
        body.classList.remove('nav-topbar');
        active = false;
      }
      return;
    }

    // The theme editor replaces section nodes on edit; a cached reference goes
    // detached and reports bottom === 0, which would latch nav-topbar on forever
    // and hide the sidebar for the merchant. Re-query when that happens.
    if (!hero || !hero.isConnected) {
      hero = findHero();
      if (!hero) return;
    }

    var bottom = hero.getBoundingClientRect().bottom;

    if (!active && bottom <= 0) {
      body.classList.add('nav-topbar');
      active = true;
      publishHeaderHeight();
    } else if (active && bottom >= RELEASE_SLOP) {
      body.classList.remove('nav-topbar');
      active = false;
    }
  }

  function onScroll() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(apply);
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });

  // The script is deferred, so it runs before the browser finishes restoring
  // scroll position on a deep reload — without these you get one frame of
  // sidebar before it snaps. `pageshow` also covers back/forward cache restores.
  window.addEventListener('load', apply);
  window.addEventListener('pageshow', apply);

  // Theme editor: sections are swapped in and out live.
  document.addEventListener('shopify:section:load', onScroll);
  document.addEventListener('shopify:section:unload', onScroll);

  // Set the correct state immediately too.
  apply();
})();

/* --------------------------------------------------------------------------
   Smooth scrolling (Lenis)

   CSS `scroll-behavior: smooth` deliberately does NOT apply to wheel/trackpad
   input — browsers only use it for programmatic scrolls — so it cannot produce
   the eased, weighted feel. Lenis intercepts wheel input and interpolates it.

   It drives the real window scroll position (rather than transforming a
   wrapper), so native `scroll` events still fire and `getBoundingClientRect()`
   stays accurate. That matters here: the homepage nav swap above and the
   theme's own overlap-navigation.js both read geometry on scroll.

   Touch is left native on purpose — momentum scrolling is already smooth on
   touch devices and hijacking it makes phones feel laggy.
   -------------------------------------------------------------------------- */

(function () {
  'use strict';

  if (typeof window.Lenis !== 'function') return;   // vendored file failed to load

  // Honour the OS "reduce motion" setting: this is exactly the kind of
  // non-essential motion that setting exists to switch off.
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  var lenis = new window.Lenis({
    duration: 0.8,           // higher = more glide. 1.05 read as too floaty; 0.8
                             // keeps the ease but tightens the tail. Useful range
                             // is roughly 0.6 (snappy) to 1.2 (very floaty).
    smoothWheel: true,
    syncTouch: false,        // leave touch devices on native momentum
    easing: function (t) { return Math.min(1, 1.001 - Math.pow(2, -10 * t)); }
  });

  function raf(time) {
    lenis.raf(time);
    window.requestAnimationFrame(raf);
  }
  window.requestAnimationFrame(raf);

  // Expose it so anchor links / "Back to top" can be routed through Lenis later
  // if wanted, and so it can be inspected from the console.
  window.__lenis = lenis;
})();

/* --------------------------------------------------------------------------
   Hero scroll indicator

   The hero now fills the viewport, which hides the fact that there is more
   page below it, so this restores the cue. `sections/image-banner.liquid` has
   no scroll-down setting of its own — the theme's only implementation belongs
   to `horizontal-scrolling-banners`, so the CSS borrows its keyframes (see
   custom-styles.css) while the markup is built here.

   Built as a real <button> rather than an <a href="#..."> on purpose:
   global.js:363-438 installs a delegated handler on every anchor link that
   drives window.scrollTo() on each frame, which would fight Lenis writing the
   same property from its own loop.
   -------------------------------------------------------------------------- */

(function () {
  'use strict';

  var heroSection = document.querySelector('main .shopify-section:first-child');
  if (!heroSection || !heroSection.querySelector('.overlapping-section')) return; // homepage only
  var wrapper = heroSection.querySelector('.banner__wrapper');
  if (!wrapper) return;

  var btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'hero-scroll-cue';
  btn.setAttribute('aria-label', 'Scroll to products');
  btn.innerHTML = '<span class="hero-scroll-cue__arrow" aria-hidden="true"></span>';
  wrapper.appendChild(btn);

  btn.addEventListener('click', function () {
    // Resolved at runtime, so reordering sections cannot break it.
    var target = heroSection.nextElementSibling;
    if (!target) return;
    if (window.__lenis && typeof window.__lenis.scrollTo === 'function') {
      window.__lenis.scrollTo(target);
    } else {
      // Lenis exits early under prefers-reduced-motion and if its asset fails.
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  });

  // Fade the cue out as soon as the visitor starts scrolling; bring it back at
  // the very top. Cheap enough to piggyback on the existing rAF-throttled read.
  var hidden = false;
  function update() {
    var past = window.scrollY > 40;
    if (past !== hidden) {
      hidden = past;
      btn.classList.toggle('hero-scroll-cue--hidden', past);
    }
  }
  var ticking = false;
  window.addEventListener('scroll', function () {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(function () { ticking = false; update(); });
  }, { passive: true });
  update();
})();
